import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { RoleBadge } from '../components/RoleBadge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import api from '../utils/api';
import { toast } from 'sonner';
import { Search, Download, TrendingUp, TrendingDown, CheckCircle, AlertCircle, ArrowUp, ArrowDown, Minus } from 'lucide-react';

// Moved outside component to avoid re-creation on each render
const DeltaIndicator = ({ value }) => {
  if (value > 0) {
    return (
      <span className="flex items-center gap-1 text-green-600 text-xs">
        <ArrowUp className="h-3 w-3" />+{value}
      </span>
    );
  }
  if (value < 0) {
    return (
      <span className="flex items-center gap-1 text-red-600 text-xs">
        <ArrowDown className="h-3 w-3" />{value}
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-gray-400 text-xs">
      <Minus className="h-3 w-3" />0
    </span>
  );
};

export const TeamOverview = () => {
  const [teamStats, setTeamStats] = useState([]);
  const [filteredStats, setFilteredStats] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchTeamStats = useCallback(async () => {
    try {
      const response = await api.get('/team/overview');
      setTeamStats(response.data);
    } catch (error) {
      toast.error('Failed to load team data');
    }
  }, []);

  const filterStats = useCallback(() => {
    if (!searchTerm) {
      setFilteredStats(teamStats);
      return;
    }

    const filtered = teamStats.filter(member =>
      member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.role.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredStats(filtered);
  }, [teamStats, searchTerm]);

  useEffect(() => {
    fetchTeamStats();
  }, [fetchTeamStats]);

  useEffect(() => {
    filterStats();
  }, [filterStats]);

  const exportToCSV = () => {
    const headers = [
      'Name', 'Username', 'Role', 'Week Apps', 'Week BP', 'Week Recruits', 
      'Week Prospects', 'S2D Status', 'Logged Today', 'Inactive 7 Days',
      'Hunt Today', 'Farm Today', 'Cultivate Today', 'Presentations Today', 'Objections Today',
      'Hunt Delta', 'Farm Delta', 'Cultivate Delta', 'Presentations Delta', 'Objections Delta'
    ];
    
    const csvData = filteredStats.map(member => [
      member.name,
      member.username,
      member.role.replace('_', ' '),
      member.week_apps,
      member.week_bp,
      member.week_recruits,
      member.week_prospects,
      member.s2d_status,
      member.logged_today ? 'Yes' : 'No',
      member.inactive_7_days ? 'Yes' : 'No',
      member.today?.hunt_messages || 0,
      member.today?.farm_messages || 0,
      member.today?.cultivate_messages || 0,
      member.today?.presentations_given || 0,
      member.today?.objections_logged || 0,
      member.delta?.hunt_messages || 0,
      member.delta?.farm_messages || 0,
      member.delta?.cultivate_messages || 0,
      member.delta?.presentations_given || 0,
      member.delta?.objections_logged || 0
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `team-overview-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('CSV exported successfully!');
  };

  const getRowClassName = (member) => {
    if (member.s2d_status === 'Achieved') {
      return 'bg-green-50 hover:bg-green-100';
    }
    if (member.inactive_7_days) {
      return 'bg-yellow-50 hover:bg-yellow-100';
    }
    return 'hover:bg-gray-50';
  };

  return (
    <div className="space-y-6" data-testid="team-overview-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-[#333333]">Team Overview</h1>
        <Button
          onClick={exportToCSV}
          className="bg-[#333333] hover:bg-[#1A1A1A] flex items-center gap-2"
          data-testid="export-csv-button"
        >
          <Download className="h-4 w-4" />
          Export CSV
        </Button>
      </div>

      {/* Legend */}
      <div className="flex gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-100 border border-green-300 rounded"></div>
          <span>S2D Achieved</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-yellow-100 border border-yellow-300 rounded"></div>
          <span>Inactive 7+ Days</span>
        </div>
      </div>

      <Card className="p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by name, username, or role..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="search-team-input"
          />
        </div>
      </Card>

      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="sticky left-0 bg-white z-10">Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-right">Week Apps</TableHead>
                <TableHead className="text-right">Week BP</TableHead>
                <TableHead className="text-right">Recruits</TableHead>
                <TableHead className="text-center border-l">Hunt</TableHead>
                <TableHead className="text-center">Farm</TableHead>
                <TableHead className="text-center">Cultivate</TableHead>
                <TableHead className="text-center">Presentations</TableHead>
                <TableHead className="text-center border-r">Objections</TableHead>
                <TableHead>S2D</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredStats.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} className="text-center text-gray-500 py-8">
                    No team members found
                  </TableCell>
                </TableRow>
              ) : (
                filteredStats.map((member) => (
                  <TableRow 
                    key={member.id}
                    data-testid={`team-member-row-${member.id}`}
                    className={getRowClassName(member)}
                  >
                    <TableCell className="font-medium sticky left-0 bg-inherit">
                      <div>
                        <span>{member.name}</span>
                        <span className="text-xs text-gray-500 block">@{member.username}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <RoleBadge role={member.role} />
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        {member.logged_today ? (
                          <Badge className="bg-green-100 text-green-700 text-xs">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Active
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-600 text-xs">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            No Log
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right" data-testid={`member-apps-${member.id}`}>
                      {member.week_apps}
                    </TableCell>
                    <TableCell className="text-right" data-testid={`member-bp-${member.id}`}>
                      ${member.week_bp.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right" data-testid={`member-recruits-${member.id}`}>
                      {member.week_recruits}
                    </TableCell>
                    
                    {/* Today's Activity with Deltas */}
                    <TableCell className="text-center border-l">
                      <div className="flex flex-col items-center">
                        <span className="font-semibold">{member.today?.hunt_messages || 0}</span>
                        <DeltaIndicator value={member.delta?.hunt_messages || 0} />
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex flex-col items-center">
                        <span className="font-semibold">{member.today?.farm_messages || 0}</span>
                        <DeltaIndicator value={member.delta?.farm_messages || 0} />
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex flex-col items-center">
                        <span className="font-semibold">{member.today?.cultivate_messages || 0}</span>
                        <DeltaIndicator value={member.delta?.cultivate_messages || 0} />
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex flex-col items-center">
                        <span className="font-semibold">{member.today?.presentations_given || 0}</span>
                        <DeltaIndicator value={member.delta?.presentations_given || 0} />
                      </div>
                    </TableCell>
                    <TableCell className="text-center border-r">
                      <div className="flex flex-col items-center">
                        <span className="font-semibold">{member.today?.objections_logged || 0}</span>
                        <DeltaIndicator value={member.delta?.objections_logged || 0} />
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <Badge 
                        variant={member.s2d_status === 'Achieved' ? 'default' : 'secondary'}
                        className={member.s2d_status === 'Achieved' ? 'bg-green-600' : ''}
                        data-testid={`member-s2d-${member.id}`}
                      >
                        {member.s2d_status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-5 w-5 text-[#E31837]" />
            <span className="text-sm text-gray-600">Total Team Members</span>
          </div>
          <p className="text-3xl font-bold text-[#333333]" data-testid="total-members">{filteredStats.length}</p>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-5 w-5 text-green-600" />
            <span className="text-sm text-gray-600">S2D Achieved</span>
          </div>
          <p className="text-3xl font-bold text-green-600" data-testid="s2d-achieved">
            {filteredStats.filter(m => m.s2d_status === 'Achieved').length}
          </p>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="h-5 w-5 text-blue-600" />
            <span className="text-sm text-gray-600">Logged Today</span>
          </div>
          <p className="text-3xl font-bold text-blue-600" data-testid="logged-today">
            {filteredStats.filter(m => m.logged_today).length}
          </p>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="h-5 w-5 text-yellow-600" />
            <span className="text-sm text-gray-600">Inactive 7+ Days</span>
          </div>
          <p className="text-3xl font-bold text-yellow-600" data-testid="inactive-count">
            {filteredStats.filter(m => m.inactive_7_days).length}
          </p>
        </Card>
      </div>

      {/* Today's Team Totals */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4 text-[#E31837]">Today's Team Activity</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-red-50 p-4 rounded-lg border border-red-200 text-center">
            <p className="text-sm text-red-700 font-medium">Hunt Messages</p>
            <p className="text-2xl font-bold text-red-600">
              {filteredStats.reduce((sum, m) => sum + (m.today?.hunt_messages || 0), 0)}
            </p>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 text-center">
            <p className="text-sm text-yellow-700 font-medium">Farm Messages</p>
            <p className="text-2xl font-bold text-yellow-600">
              {filteredStats.reduce((sum, m) => sum + (m.today?.farm_messages || 0), 0)}
            </p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
            <p className="text-sm text-green-700 font-medium">Cultivate Messages</p>
            <p className="text-2xl font-bold text-green-600">
              {filteredStats.reduce((sum, m) => sum + (m.today?.cultivate_messages || 0), 0)}
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
            <p className="text-sm text-blue-700 font-medium">Presentations</p>
            <p className="text-2xl font-bold text-blue-600">
              {filteredStats.reduce((sum, m) => sum + (m.today?.presentations_given || 0), 0)}
            </p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 text-center">
            <p className="text-sm text-purple-700 font-medium">Objections</p>
            <p className="text-2xl font-bold text-purple-600">
              {filteredStats.reduce((sum, m) => sum + (m.today?.objections_logged || 0), 0)}
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};
